/**
 * @file UpdateInvoiceFromSuppliedDialog.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-08
 * @modified 2026-10-08
 *
 * "Update invoice from supplied qty…" in the bill window (Cam, 8 Oct 2026).
 * When a bill's supplied quantities were saved but the invoice edit offered
 * with them was refused (paid since, a locked date, Xero said no), the
 * changes the order's invoice still needs are offered again here: GET
 * /api/bills/:id/invoice-proposals, shown in InvoiceProposalDialog, applied
 * with POST /api/bills/:id/invoice-proposals/apply (the ordinary invoice
 * edit, reason "Supplied quantity changed from bill <no>", guarded on the
 * lines shown). Each invoice can also be opened in the invoice editor.
 *
 * 1.1.0 (2026-10-08, review): also from the order (Add / Update Supplied Qty,
 * the order window — /api/orders/customer-orders/:id/invoice-proposals) and
 * the PO window (/api/purchase-orders/:id/invoice-proposals): `baseUrl`.
 */

import React, { useEffect, useState } from 'react';
import { API_URL } from '../config';
import InvoiceProposalDialog from './InvoiceProposalDialog';
import AlertDialog from './AlertDialog';
import { applyAnswer, openInvoiceEditor } from '../utils/invoiceProposals';

/**
 * @param {object} props
 * @param {number} [props.billId] — the bill window's (else `baseUrl`)
 * @param {string} [props.baseUrl] — …/invoice-proposals of an order or a PO
 * @param {() => void} props.onClose
 * @param {(message: string, type?: string) => void} [props.onDone]
 * @param {(menu: string) => void} [props.onNavigate] — for "open the invoice"
 */
function UpdateInvoiceFromSuppliedDialog({ billId, baseUrl, onClose, onDone, onNavigate }) {
  const url = baseUrl || `${API_URL}/api/bills/${billId}/invoice-proposals`;
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [stale, setStale] = useState(false);

  const load = () => fetch(url, { credentials: 'include' })
    .then(r => r.json())
    .then(d => {
      if (!d.success) { setError(d.error || 'The invoice changes could not be worked out.'); return; }
      setData(d);
    })
    .catch(err => setError(`The invoice changes could not be worked out: ${err.message}`));

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [url]);

  const apply = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`${url}/apply`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ invoices: applyAnswer(data.proposals).expected })
      });
      const d = await res.json().catch(() => ({}));
      if (res.status === 409 && d.stale) {
        setStale(true);
        setData(prev => ({ ...prev, proposals: d.proposals || [] }));
        return;
      }
      if (!res.ok || !d.success) { setError(d.error || 'The invoice could not be changed.'); return; }
      const u = d.invoice_update || {};
      onDone && onDone(u.message || 'Done.', u.refused?.length ? 'warning' : 'success');
      onClose();
    } catch (err) {
      setError(`The invoice could not be changed: ${err.message}`);
    } finally {
      setBusy(false);
    }
  };

  if (error && !data) return <AlertDialog isOpen type="error" message={error} onClose={onClose} />;
  if (!data) return null;
  if (!data.proposals?.length) {
    const note = (data.notices || []).map(n => n.blocked || (n.unmatched?.length ? `${n.invoice_no}: a line could not be matched to the order — check it by hand` : null)).filter(Boolean).join(' ');
    return <AlertDialog isOpen type="info" message={note || 'No invoice needs changing for these supplied quantities (or it has already gone out to the customer).'} onClose={onClose} />;
  }
  return (
    <InvoiceProposalDialog
      proposals={data.proposals}
      notices={data.notices}
      stale={stale}
      busy={busy}
      error={error}
      applyLabel="Apply these changes"
      continueLabel="Not now"
      onApply={apply}
      onContinue={onClose}
      onOpenInvoice={onNavigate ? (p) => { onClose(); openInvoiceEditor(p.invoice_id, onNavigate); } : null}
    />
  );
}

export default UpdateInvoiceFromSuppliedDialog;
